import { nextDesignation, prefixFor } from "./designation.ts";
import type { Device, Terminal, TerminalKind } from "./entities.ts";
import { deviceId, newId, terminalId } from "./ids.ts";

/**
 * Strukturell subset av SymbolDefinition som factoryn behöver.
 * `packages/domain` får inte importera från `packages/symbols` (symbols beror på domain).
 */
export type SymbolTerminalInput = {
  id: string;
  kind: TerminalKind;
  label: string;
};

export type SymbolInput = {
  deviceKind: string;
  designationPrefix?: string;
  terminals: ReadonlyArray<SymbolTerminalInput>;
};

export type CreateDeviceFromSymbolOpts = {
  existingDevices: Iterable<Pick<Device, "designation">>;
  attributes?: Record<string, unknown>;
  symbolId?: string;
};

/**
 * Ren factory — inga Yjs-sidoeffekter. Anroparen persister:ar själv via putDevice/putTerminal.
 * Garanterar:
 *  - device.id är nygenererat
 *  - device.designation är nästa lediga för prefixet (t.ex. K3)
 *  - en Terminal per SymbolTerminalInput med stabil symbolTerminalId-referens
 *  - device.terminals listar terminalerna i samma ordning som symbolen deklarerar dem
 */
export function createDeviceFromSymbol(
  symbol: SymbolInput,
  opts: CreateDeviceFromSymbolOpts,
): { device: Device; terminals: Terminal[] } {
  const prefix = prefixFor(symbol.deviceKind, symbol.designationPrefix);
  const designation = nextDesignation(prefix, opts.existingDevices);
  const dId = deviceId(newId("dev"));

  const terminals: Terminal[] = symbol.terminals.map((t) => ({
    id: terminalId(newId("term")),
    deviceId: dId,
    kind: t.kind,
    label: t.label,
    symbolTerminalId: t.id,
  }));

  const attributes: Record<string, unknown> = { ...(opts.attributes ?? {}) };
  if (opts.symbolId !== undefined) attributes["symbolId"] = opts.symbolId;

  const device: Device = {
    id: dId,
    deviceKind: symbol.deviceKind,
    designation,
    terminals: terminals.map((t) => t.id),
    attributes,
  };

  return { device, terminals };
}
